import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { HazardData, HazardMapData } from '../types/HazardData';
import HazardPopup from './HazardPopup.tsx';
import SeismicHazardPopup from './SeismicHazardPopup.tsx';
import { createRoot, Root } from 'react-dom/client';
import 'leaflet-easyprint';

// Importa plugin per Leaflet
declare global {
  interface Window {
    easyPrint: any;
  }
}

interface HazardMapProps {
  probabilityData: HazardData[];
  hazardData: HazardMapData[];
  lavaData: HazardData[];
  seismicData: HazardData[];
  seismicWmsData: any;
  hazardType: 'ash' | 'lava' | 'seismic';
  mapMode: 'probability' | 'hazard' | 'wms';
  opacity: number;
  loading: boolean;
  error: string | null;
  onDownloadHazardCurve?: (hazardCurveData: any, lat: number, lon: number, all_percentiles_curves?: any) => void;
  onBboxChange?: (bbox: string) => void;
}

const HazardMap: React.FC<HazardMapProps> = ({ probabilityData, hazardData, lavaData, seismicData, seismicWmsData, hazardType, mapMode, opacity, loading, error, onDownloadHazardCurve, onBboxChange }) => {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const layerGroupRef = useRef<L.LayerGroup | null>(null);
  const activeWmsLayerRef = useRef<L.TileLayer.WMS | null>(null);
  const activeSeismicWmsLayerRef = useRef<L.TileLayer.WMS | null>(null);
  const [mouseCoordinates, setMouseCoordinates] = useState<{lat: number, lon: number} | null>(null);


  // Funzione per aggiungere controlli mappa
  const addMapControls = (map: L.Map) => {
    console.log('addMapControls called');
    console.log('L available:', !!(window as any).L);
    console.log('easyPrint available:', !!((window as any).L && (window as any).L.easyPrint));
    
    // Aspetta un momento per assicurarsi che tutto sia caricato
    setTimeout(() => {
      console.log('Attempting to add easyPrint after timeout...');
      console.log('L.easyPrint:', (window as any).L?.easyPrint);
      
      // Controlla se leaflet-easyprint è disponibile
      if ((window as any).L && (window as any).L.easyPrint) {
        try {
          console.log('Creating easyPrint control...');
          
          // Configura leaflet-easyprint con opzioni complete
          const easyPrintOptions = {
            title: 'Download Map',
            position: 'topright' as const,
            sizeModes: ['A4Portrait', 'A4Landscape', 'Current'],
            filename: `hazard_map_${new Date().toISOString().split('T')[0]}`,
            exportOnly: true, // Solo esportazione, non stampa diretta
            tileWait: 2000, // Attendi fino a 2 secondi per il caricamento delle tile
            hideControlContainer: false, // Non nascondere l'intero contenitore dei controlli
            hideClasses: ['leaflet-control-zoom'], // Nascondi solo il controllo zoom
            customWindowTitle: 'Hazard Map Print', 
            spinnerBgColor: '#0DC5DC', // Colore di sfondo dello spinner
            customSpinnerClass: 'epLoader', // Classe CSS personalizzata per lo spinner
            defaultSizeTitles: {
              Current: 'Current Size',
              A4Landscape: 'A4 Landscape', 
              A4Portrait: 'A4 Portrait'
            }
          };

          // Crea il controllo easyPrint
          const printer = (window as any).L.easyPrint(easyPrintOptions); // Usa L.easyPrint dal namespace globale
          map.addControl(printer); // Aggiungi il controllo alla mappa
          
          console.log('EasyPrint control created and added to map');
        } catch (error) {
          console.error('Error creating easyPrint control:', error);
        }
      } else {
        console.warn('leaflet-easyprint not available after timeout');
      }
    }, 500);
    
    // Fallback al controllo personalizzato se easyprint non è disponibile
    setTimeout(() => {
      if (!(window as any).L || !(window as any).L.easyPrint) {
        console.warn('leaflet-easyprint not available, adding fallback control');
      
      // Fallback al controllo personalizzato se easyprint non è disponibile
      const downloadControl = L.Control.extend({
        onAdd: function(map: L.Map) {
          const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
          container.style.backgroundColor = 'white';
          container.style.width = '30px';
          container.style.height = '30px';
          container.style.cursor = 'pointer';
          container.innerHTML = '<i class="fas fa-download" style="line-height: 30px; text-align: center; width: 30px; display: block; color: #333;"></i>';
          container.title = 'Download map as image (fallback mode)';
          
          container.onclick = function() { // Cosa succede al click
            // Usa la logica html2canvas come fallback
            const captureMapWithAllLayers = async () => {
              try {
                const mapContainer = map.getContainer();
                
                if ((window as any).html2canvas) {
                  try {
                    const mapCanvas = await (window as any).html2canvas(mapContainer, {
                      useCORS: true,
                      allowTaint: true,
                      foreignObjectRendering: true,
                      scale: 1,
                      logging: false,
                      backgroundColor: null,
                      imageTimeout: 15000
                    });
                    
                    const link = document.createElement('a');
                    link.download = `hazard_map_fallback_${new Date().toISOString().split('T')[0]}.png`;
                    link.href = mapCanvas.toDataURL('image/png', 1.0);
                    link.click();
                    
                  } catch (error) {
                    console.error('Fallback download failed:', error);
                    alert('Download failed. Please check if all map layers are loaded.');
                  }
                } else {
                  alert('Download functionality requires html2canvas library.');
                }
              } catch (error) {
                console.error('Download error:', error);
                alert('Download failed. Please try again.');
              }
            };
            
            captureMapWithAllLayers();
          };
          
          return container;
        }
      });

        new downloadControl({ position: 'topright' }).addTo(map);
      }
    }, 1000);
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current).setView([37.5, 15.0], 8);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(mapRef.current);

    layerGroupRef.current = L.layerGroup().addTo(mapRef.current);

    // Aggiungi controlli per download
    addMapControls(mapRef.current);
    
    // Aggiungi draw controls per bounding box
    if (onBboxChange) {
      const drawnItems = new L.FeatureGroup();
      mapRef.current.addLayer(drawnItems);
      
      const drawControl = new (L.Control as any).Draw({
        position: 'topleft',
        draw: {
          rectangle: {
            shapeOptions: {
              color: '#ff7800',
              weight: 2,
              fillOpacity: 0.1
            }
          },
          polyline: false,
          polygon: false,
          circle: false,
          marker: false,
          circlemarker: false
        },
        edit: {
          featureGroup: drawnItems,
          remove: true
        }
      });
      
      mapRef.current.addControl(drawControl);
      
      // Event listeners per draw
      mapRef.current.on(L.Draw.Event.CREATED, (e: any) => {
        const layer = e.layer;
        drawnItems.clearLayers();
        drawnItems.addLayer(layer);
        
        if (e.layerType === 'rectangle') {
          const bounds = layer.getBounds();
          const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
          onBboxChange(bbox);
        }
      });
      
      mapRef.current.on(L.Draw.Event.DELETED, () => {
        onBboxChange('');
      });
    }
    
    // Apply opacity to entire layer group
    const layerElement = layerGroupRef.current.getPane();
    if (layerElement) {
      (layerElement as HTMLElement).style.opacity = opacity.toString();
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Update only active layer opacity smoothly without layer refresh
  useEffect(() => {
    // Per layer WMS (lava)
    if (activeWmsLayerRef.current) {
      activeWmsLayerRef.current.setOpacity(opacity);
    }
    
    // Per layer WMS seismic
    if (activeSeismicWmsLayerRef.current) {
      activeSeismicWmsLayerRef.current.setOpacity(opacity);
    }
    
    // Per layer vettoriali (ash, seismic) nel layerGroup
    if (layerGroupRef.current) {
      const layerElement = layerGroupRef.current.getPane();
      if (layerElement) {
        (layerElement as HTMLElement).style.opacity = opacity.toString();
      }
    }
  }, [opacity]);

  // Update map data
  useEffect(() => {
    if (!mapRef.current || !layerGroupRef.current) return;

    // Clear existing layers
    layerGroupRef.current.clearLayers();
    
    // Reset riferimento WMS layer
    activeWmsLayerRef.current = null;
    activeSeismicWmsLayerRef.current = null;

    // Se è lava, usa WMS layer invece di punti discreti
    if (hazardType === 'lava' && lavaData.length > 0 && (lavaData[0] as any).wms_config) {
      const wmsConfig = (lavaData[0] as any).wms_config;
      
      const wmsLayer = L.tileLayer.wms(wmsConfig.url, {
        layers: wmsConfig.layers,
        format: wmsConfig.format,
        transparent: wmsConfig.transparent,
        version: wmsConfig.version,
        crs: L.CRS.EPSG4326,
        opacity: opacity
      });
      
      // Salva il riferimento al layer WMS attivo
      activeWmsLayerRef.current = wmsLayer;
      layerGroupRef.current.addLayer(wmsLayer);
      
      // Aggiungi event listener per GetFeatureInfo
      const onMapClick = (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;
        
        // Costruisci URL per GetFeatureInfo
        const map = mapRef.current!;
        const size = map.getSize();
        const bounds = map.getBounds();
        const point = map.latLngToContainerPoint(e.latlng);
        
        // Usa coordinate geografiche per il BBOX
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        
        const getFeatureInfoUrl = `${wmsConfig.url}?` +
          `SERVICE=WMS&` +
          `VERSION=1.3.0&` +
          `REQUEST=GetFeatureInfo&` +
          `LAYERS=${wmsConfig.layers}&` +
          `QUERY_LAYERS=${wmsConfig.layers}&` +
          `STYLES=&` +
          `BBOX=${sw.lat},${sw.lng},${ne.lat},${ne.lng}&` +
          `FEATURE_COUNT=10&` +
          `HEIGHT=${size.y}&` +
          `WIDTH=${size.x}&` +
          `FORMAT=image/png&` +
          `INFO_FORMAT=application/json&` +
          `CRS=EPSG:4326&` +
          `I=${Math.round(point.x)}&` +
          `J=${Math.round(point.y)}`;
        
        // Log dell'URL per debug
        console.log('GetFeatureInfo URL:', getFeatureInfoUrl);
        
        // Esegui richiesta GetFeatureInfo
        fetch(getFeatureInfoUrl)
          .then(response => response.json())
          .then(data => {
            console.log('GetFeatureInfo response:', data);
            
            // Estrai il valore dal risultato GeoServer
            let hazardValue = null;
            if (data.features && data.features.length > 0) {
              const feature = data.features[0];
              // Il nome della proprietà dipende dal layer, cerchiamo quello che contiene un valore numerico
              const properties = feature.properties;
              for (const key in properties) {
                const value = properties[key];
                if (typeof value === 'number' && !isNaN(value)) {
                  hazardValue = value;
                  break;
                }
              }
            }
            
            if (hazardValue !== null) {
              // Mostra popup con il valore
              const popupContent = `
                <div style="font-family: Arial, sans-serif;">
                  <h4>Lavaflow invasion exceedance probability in 30 years</h4>
                  <p><strong>Location:</strong> ${lat.toFixed(4)}°, ${lng.toFixed(4)}°</p>
                  <p><strong>Probability:</strong> ${(hazardValue * 100).toFixed(2)}%</p>
                </div>
              `;
              
              L.popup()
                .setLatLng(e.latlng)
                .setContent(popupContent)
                .openOn(map);
            } else {
              // Nessun dato disponibile per questo punto
              L.popup()
                .setLatLng(e.latlng)
                .setContent(`
                  <div style="font-family: Arial, sans-serif;">
                    <h4>No Data</h4>
                    <p>No hazard data available at this location.</p>
                  </div>
                `)
                .openOn(map);
            }
          })
          .catch(error => {
            console.error('Error fetching GetFeatureInfo:', error);
            L.popup()
              .setLatLng(e.latlng)
              .setContent(`
                <div style="font-family: Arial, sans-serif;">
                  <h4>Error</h4>
                  <p>Unable to fetch data for this location.</p>
                </div>
              `)
              .openOn(map);
          });
      };
      
      // Rimuovi event listener esistenti e aggiungi il nuovo
      mapRef.current.off('click');
      mapRef.current.on('click', onMapClick);
      
      return;
    }
    
    // Se è seismic WMS, usa WMS layer invece di punti discreti
    if (hazardType === 'seismic' && mapMode === 'wms' && seismicWmsData && seismicWmsData.wms_config) {
      const wmsConfig = seismicWmsData.wms_config;
      
      const wmsLayer = L.tileLayer.wms(wmsConfig.url, {
        layers: wmsConfig.layers,
        format: wmsConfig.format,
        transparent: wmsConfig.transparent,
        version: wmsConfig.version,
        crs: wmsConfig.srs === 'EPSG:3857' ? L.CRS.EPSG3857 : L.CRS.EPSG4326,
        opacity: opacity
      });
      
      // Salva il riferimento al layer WMS seismic attivo
      activeSeismicWmsLayerRef.current = wmsLayer;
      layerGroupRef.current.addLayer(wmsLayer);
      
      // Aggiungi event listener per GetFeatureInfo seismic
      const onSeismicMapClick = (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;
        
        // Costruisci URL per GetFeatureInfo
        const map = mapRef.current!;
        const size = map.getSize();
        const bounds = map.getBounds();
        const point = map.latLngToContainerPoint(e.latlng);
        
        // Usa coordinate geografiche per il BBOX
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        
        const getFeatureInfoUrl = `${wmsConfig.url}?` +
          `SERVICE=WMS&` +
          `VERSION=1.3.0&` +
          `REQUEST=GetFeatureInfo&` +
          `LAYERS=${wmsConfig.layers}&` +
          `QUERY_LAYERS=${wmsConfig.layers}&` +
          `STYLES=&` +
          `BBOX=${sw.lat},${sw.lng},${ne.lat},${ne.lng}&` +
          `FEATURE_COUNT=10&` +
          `HEIGHT=${size.y}&` +
          `WIDTH=${size.x}&` +
          `FORMAT=image/png&` +
          `INFO_FORMAT=application/json&` +
          `CRS=EPSG:4326&` +
          `I=${Math.round(point.x)}&` +
          `J=${Math.round(point.y)}`;
        
        // Log dell'URL per debug
        console.log('Seismic GetFeatureInfo URL:', getFeatureInfoUrl);
        
        // Esegui richiesta GetFeatureInfo
        fetch(getFeatureInfoUrl)
          .then(response => response.json())
          .then(data => {
            console.log('Seismic GetFeatureInfo response:', data);
            
            // Estrai il valore dal risultato GeoServer
            let hazardValue = null;
            if (data.features && data.features.length > 0) {
              const feature = data.features[0];
              // Il nome della proprietà dipende dal layer, cerchiamo quello che contiene un valore numerico
              const properties = feature.properties;
              for (const key in properties) {
                const value = properties[key];
                if (typeof value === 'number' && !isNaN(value)) {
                  hazardValue = value;
                  break;
                }
              }
            }
            
            if (hazardValue !== null) {
              // Mostra popup con il valore seismic
              const popupContent = `
                <div style="font-family: Arial, sans-serif;">
                  <h4>Seismic Hazard Intensity</h4>
                  <p><strong>Location:</strong> ${lat.toFixed(4)}°, ${lng.toFixed(4)}°</p>
                  <p><strong>Intensity:</strong> ${hazardValue.toFixed(2)}</p>
                </div>
              `;
              
              L.popup()
                .setLatLng(e.latlng)
                .setContent(popupContent)
                .openOn(map);
            } else {
              // Nessun dato disponibile per questo punto
              L.popup()
                .setLatLng(e.latlng)
                .setContent(`
                  <div style="font-family: Arial, sans-serif;">
                    <h4>No Data</h4>
                    <p>No seismic hazard data available at this location.</p>
                  </div>
                `)
                .openOn(map);
            }
          })
          .catch(error => {
            console.error('Error fetching seismic GetFeatureInfo:', error);
            L.popup()
              .setLatLng(e.latlng)
              .setContent(`
                <div style="font-family: Arial, sans-serif;">
                  <h4>Error</h4>
                  <p>Unable to fetch seismic data for this location.</p>
                </div>
              `)
              .openOn(map);
          });
      };
      
      // Rimuovi event listener esistenti e aggiungi il nuovo per seismic
      mapRef.current.off('click');
      mapRef.current.on('click', onSeismicMapClick);
      
      return;
    }
    
    // Per layer non-WMS, rimuovi event listener WMS e aggiungi click per punti discreti
    mapRef.current.off('click');    // Per altri tipi o quando lavaData non ha wms_config, usa il rendering a punti/poligoni
    const currentData = hazardType === 'lava' 
      ? lavaData 
      : hazardType === 'seismic' 
      ? (mapMode === 'probability' ? seismicData : hazardData)
      : (mapMode === 'probability' ? probabilityData : hazardData);
    
    if (!currentData.length) return;

    // Get min/max values for color scaling
    const values = hazardType === 'lava'
      ? lavaData.map(d => d.probability)
      : hazardType === 'seismic'
      ? (mapMode === 'probability' 
        ? seismicData.map(d => d.probability)
        : hazardData.map(d => d.hazard))
      : (mapMode === 'probability' 
        ? probabilityData.map(d => d.probability)
        : hazardData.map(d => d.hazard));
    
    // Check if we have valid values
    if (values.length === 0) return;
    
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    
    // Stampa valori per seismic hazard maps
    if (hazardType === 'seismic' && mapMode === 'hazard') {
      console.log('=== SEISMIC HAZARD MAP DATA ===');
      console.log('Data points:', currentData.length);
      console.log('Values range:', `${minValue.toFixed(4)} - ${maxValue.toFixed(4)}`);
      console.log('Sample values (first 10):', values.slice(0, 10).map(v => v.toFixed(4)));
      console.log('Sample coordinates:', currentData.slice(0, 5).map(d => `(${d.lat.toFixed(4)}, ${d.lon.toFixed(4)})`));
      
      // Analisi distribuzione valori
      const uniqueValues = [...new Set(values)].sort((a, b) => a - b);
      const valueDistribution = uniqueValues.map(val => ({
        value: val,
        count: values.filter(v => v === val).length,
        percentage: ((values.filter(v => v === val).length / values.length) * 100).toFixed(1)
      }));
      
      console.log('Unique values:', uniqueValues.length);
      console.log('Value distribution:');
      valueDistribution.slice(0, 10).forEach(item => {
        console.log(`  ${item.value.toFixed(3)}: ${item.count} points (${item.percentage}%)`);
      });
      
      // Statistiche principali
      const sortedValues = [...values].sort((a, b) => a - b);
      const median = sortedValues[Math.floor(sortedValues.length / 2)];
      const q25 = sortedValues[Math.floor(sortedValues.length * 0.25)];
      const q75 = sortedValues[Math.floor(sortedValues.length * 0.75)];
      
      console.log('Statistics:');
      console.log(`  Median: ${median.toFixed(4)}`);
      console.log(`  Q25: ${q25.toFixed(4)}, Q75: ${q75.toFixed(4)}`);
      console.log(`  Values > ${minValue.toFixed(1)}: ${values.filter(v => v > minValue).length} (${((values.filter(v => v > minValue).length / values.length) * 100).toFixed(1)}%)`);
      console.log('================================');
    }
    
    // If all values are the same (e.g., all 0), skip visualization
    if (minValue === maxValue && minValue === 0) {
      console.warn('All hazard values are 0 for this threshold');
      return;
    }

    // Usa scala continua senza quantili

    // Color scale function - Viridis per tutti i tipi

    // Funzione per discretizzare valori sismici interpolati per la visualizzazione
    const discretizeSeismicValue = (interpolatedValue: number): number => {
      // Valori grezzi della griglia originale (senza interpolazione)
      const gridValues = [6, 7, 8, 9, 10]; // Estendi se necessario
      
      // Trova il valore griglia più vicino
      let closestValue = gridValues[0];
      let minDistance = Math.abs(interpolatedValue - closestValue);
      
      for (const gridValue of gridValues) {
        const distance = Math.abs(interpolatedValue - gridValue);
        if (distance < minDistance) {
          minDistance = distance;
          closestValue = gridValue;
        }
      }
      
      return closestValue;
    };

    const getColor = (value: number, min: number, max: number, hazardType: 'ash' | 'lava' | 'seismic'): string => {
      const normalizedMax = max;
      
      if (normalizedMax === min || value <= 0) {
        return hazardType === 'seismic' ? 'rgb(48, 18, 59)' : 'rgb(68, 1, 84)';
      }
      
      // Per dati sismici nella visualizzazione, discretizza il valore interpolato
      let displayValue = value;
      if (hazardType === 'seismic') {
        displayValue = discretizeSeismicValue(value);
        console.log(`Seismic visualization: interpolated=${value.toFixed(3)} -> discrete=${displayValue}`);
      }
      
      // Scegli la colormap in base al tipo di hazard
      let colors: number[][];
      if (hazardType === 'seismic') {
        // Scala colori turbo dal SLD GeoServer (10 classi)
        colors = [
          [0.188, 0.071, 0.232], // #30123b - viola scuro (6.83-6.97)
          [0.275, 0.384, 0.847], // #4662d8 - blu (6.97-7.00)
          [0.208, 0.671, 0.973], // #35abf8 - ciano (7.00-7.26)
          [0.106, 0.894, 0.710], // #1be5b5 - turchese (7.26-7.47)
          [0.455, 0.996, 0.365], // #74fe5d - verde (7.47-7.59)
          [0.788, 0.937, 0.204], // #c9ef34 - giallo-verde (7.59-7.70)
          [0.984, 0.725, 0.220], // #fbb938 - giallo (7.70-7.81)
          [0.961, 0.412, 0.094], // #f56918 - arancione (7.81-7.91)
          [0.788, 0.161, 0.012], // #c92903 - rosso (7.91-8.28)
          [0.478, 0.016, 0.012]  // #7a0403 - rosso scuro (8.28-10.17)
        ];
      } else {
        // Viridis color scale per ash e lava
        colors = [
          [0.267004, 0.004874, 0.329415],
          [0.282623, 0.140926, 0.457517],
          [0.253935, 0.265254, 0.529983],
          [0.206756, 0.371758, 0.553117],
          [0.163625, 0.471133, 0.558148],
          [0.127568, 0.566949, 0.550556],
          [0.134692, 0.658636, 0.517649],
          [0.266941, 0.748751, 0.440573],
          [0.477504, 0.821444, 0.318195],
          [0.741388, 0.873449, 0.149561],
          [0.993248, 0.906157, 0.143936]
        ];
      }
      
      // Normalizzazione semplificata
      const range = normalizedMax - min;
      let normalized;
      
      if (range > 0) {
        if (hazardType === 'seismic') {
          // Usa mappatura discreta per valori grezzi della griglia
          const gridValues = [6, 7, 8, 9, 10];
          const valueIndex = gridValues.indexOf(displayValue);
          if (valueIndex !== -1) {
            // Mappa uniformemente sui colori disponibili
            normalized = valueIndex / (gridValues.length - 1);
          } else {
            // Fallback a normalizzazione lineare
            normalized = Math.max(0, Math.min(1, (displayValue - min) / range));
          }
        } else {
          // Normalizzazione lineare standard per ash/lava
          normalized = Math.max(0, Math.min(1, (value - min) / range));
        }
      } else {
        normalized = 0.5;
      }
      
      const index = Math.floor(normalized * (colors.length - 1));
      const nextIndex = Math.min(index + 1, colors.length - 1);
      const t = (normalized * (colors.length - 1)) - index;
      const color1 = colors[index];
      const color2 = colors[nextIndex];
      const r = Math.round((color1[0] * (1 - t) + color2[0] * t) * 255);
      const g = Math.round((color1[1] * (1 - t) + color2[1] * t) * 255);
      const b = Math.round((color1[2] * (1 - t) + color2[2] * t) * 255);
      return `rgb(${r}, ${g}, ${b})`;
    };

    // Create grid cells
    currentData.forEach((point) => {
      const value = hazardType === 'lava'
        ? (point as HazardData).probability
        : hazardType === 'seismic'
        ? (mapMode === 'probability' 
          ? (point as HazardData).probability
          : (point as HazardMapData).hazard)
        : (mapMode === 'probability'
          ? (point as HazardData).probability
          : (point as HazardMapData).hazard);
      const color = getColor(value, minValue, maxValue, hazardType);

      // Use overlapping cells to completely eliminate borders
      const size = 0.0125; // Slightly larger for overlap
      const bounds: L.LatLngBoundsExpression = [
        [point.lat - size/2, point.lon - size/2],
        [point.lat + size/2, point.lon + size/2]
      ];

      const rectangle = L.rectangle(bounds, {
        fillColor: color,
        fillOpacity: 1, // Always full opacity for individual cells
        stroke: false,
        weight: 0,
        color: 'transparent',
        interactive: true
      });

      if (hazardType === 'lava' || mapMode === 'probability') {
        const label = hazardType === 'lava' ? 'Lava Flow Probability' : 
                      hazardType === 'seismic' ? 'Seismic Exceedance Probability' :
                      'Exceedance Probability';
        const logValue = value > 0 ? Math.log10(value).toFixed(2) : 'N/A';
        const popupContent = `<strong>Coordinates:</strong> ${point.lat.toFixed(4)}, ${point.lon.toFixed(4)}<br/>
          <strong>${label}:</strong> ${(value * 100).toFixed(2)}%<br/>
          <strong>Log₁₀(${label}):</strong> ${logValue}`;
        rectangle.bindPopup(popupContent);
      } else {
        const popupDiv = document.createElement('div');
        popupDiv.style.width = '640px';
        popupDiv.style.height = '560px';

        // Per tenere traccia del root React
        let popupRoot: Root | null = null;

        rectangle.bindPopup(popupDiv, {
          maxWidth: 640,
          minWidth: 640,
          maxHeight: 580,
          className: 'hazard-popup-large'
        });

        rectangle.on('popupopen', () => {
          popupRoot = createRoot(popupDiv);
          
          const pointData = point as HazardMapData;
          
          // Usa SeismicHazardPopup per dati sismici, HazardPopup per ash data
          if (hazardType === 'seismic') {
            popupRoot.render(
              <SeismicHazardPopup
                lat={point.lat}
                lon={point.lon}
                hazard={pointData.hazard}
                hazard_prev={pointData.hazard_prev}
                hazard_next={pointData.hazard_next}
                interpolated_coords={pointData.interpolated_coords}
                prev_coords={pointData.prev_coords}
                next_coords={pointData.next_coords}
                hazard_curve={pointData.hazard_curve as any}
                all_percentiles_curves={pointData.all_percentiles_curves as any}
                onDownload={onDownloadHazardCurve}
              />
            );
          } else {
            popupRoot.render(
              <HazardPopup
                lat={point.lat}
                lon={point.lon}
                hazard={pointData.hazard}
                hazard_prev={pointData.hazard_prev}
                hazard_next={pointData.hazard_next}
                interpolated_coords={pointData.interpolated_coords}
                prev_coords={pointData.prev_coords}
                next_coords={pointData.next_coords}
                hazard_curve={pointData.hazard_curve}
                all_percentiles_curves={pointData.all_percentiles_curves}
                onDownload={onDownloadHazardCurve}
              />
            );
          }
        });

        rectangle.on('popupclose', () => {
          if (popupRoot) {
            popupRoot.unmount();
            popupRoot = null;
          }
        });
      }

      layerGroupRef.current!.addLayer(rectangle);
    });

    // Fit map to data bounds
    if (currentData.length > 0) {
      const lats = currentData.map(d => d.lat);
      const lons = currentData.map(d => d.lon);
      const bounds: L.LatLngBoundsExpression = [
        [Math.min(...lats), Math.min(...lons)],
        [Math.max(...lats), Math.max(...lons)]
      ];
      mapRef.current.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [probabilityData, hazardData, lavaData, seismicData, hazardType, mapMode, opacity, onDownloadHazardCurve]);

  // useEffect per gestire il tracking delle coordinate del mouse
  useEffect(() => {
    if (!mapRef.current) return;

    const handleMouseMove = (e: L.LeafletMouseEvent) => {
      setMouseCoordinates({
        lat: e.latlng.lat,
        lon: e.latlng.lng
      });
    };

    const handleMouseOut = () => {
      setMouseCoordinates(null);
    };

    // Aggiungi event listeners
    mapRef.current.on('mousemove', handleMouseMove);
    mapRef.current.on('mouseout', handleMouseOut);

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.off('mousemove', handleMouseMove);
        mapRef.current.off('mouseout', handleMouseOut);
      }
    };
  }, [mapRef.current]);

  return (
    <div className="map-container" style={{ width: '100%', maxWidth: '100%', position: 'relative' }}>
      <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }}>
        {/* Sposta qui la legenda */}
        {(probabilityData.length > 0 || hazardData.length > 0 || lavaData.length > 0 || seismicData.length > 0 || seismicWmsData) && (
          <div className="legend" style={{ position: 'absolute', bottom: 20, right: 20, zIndex: 999 }}>
            <div className="legend-title">
              <i className="fas fa-palette" style={{ marginRight: '6px' }}></i>
              {hazardType === 'lava' 
                ? 'Lava Flow Probability'
                : hazardType === 'seismic'
                ? (mapMode === 'wms' ? 'Seismic Hazard Map' : mapMode === 'probability' ? 'Seismic Exceedance Probability' : 'Seismic Hazard (Intensity)')
                : (mapMode === 'probability' ? 'Exceedance Probability' : 'Hazard Value')}
            </div>
            
            {/* Usa legenda WMS per lava se disponibile */}
            {hazardType === 'lava' && lavaData.length > 0 && lavaData[0].legend_config ? (
              <div className="wms-legend">
                <img 
                  src={lavaData[0].legend_config.url} 
                  alt="Legend" 
                  style={{ maxWidth: '100%', height: 'auto' }}
                  onError={(e) => {
                    console.error('Failed to load WMS legend');
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            ) : hazardType === 'seismic' && mapMode === 'wms' && seismicWmsData && seismicWmsData.legend_config ? (
              <div className="wms-legend">
                <img 
                  src={seismicWmsData.legend_config.url} 
                  alt="Seismic Legend" 
                  style={{ maxWidth: '100%', height: 'auto' }}
                  onError={(e) => {
                    console.error('Failed to load seismic WMS legend');
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <>
                {/* Legenda manuale per altri tipi */}
                <div className="legend-scale">
                  <div className={hazardType === 'seismic' ? "legend-gradient-turbo" : "legend-gradient"}></div>
                </div>
                <div className="legend-labels">
                  {hazardType === 'lava' && lavaData.length > 0 ? (
                    <>
                      <span>{Math.min(...lavaData.map(d => d.probability)).toFixed(4)}</span>
                      <span>{Math.max(...lavaData.map(d => d.probability)).toFixed(4)}</span>
                    </>
                  ) : hazardType === 'seismic' && seismicData.length > 0 && mapMode === 'probability' ? (
                    <>
                      <span>{Math.min(...seismicData.map(d => d.probability)).toFixed(4)}</span>
                      <span>{Math.max(...seismicData.map(d => d.probability)).toFixed(4)}</span>
                    </>
                  ) : hazardType === 'seismic' && hazardData.length > 0 && mapMode !== 'probability' ? (
                    <>
                      <span>{Math.min(...hazardData.map(d => d.hazard)).toFixed(1)}</span>
                      <span>{Math.max(...hazardData.map(d => d.hazard)).toFixed(1)}</span>
                    </>
                  ) : mapMode === 'probability' && probabilityData.length > 0 ? (
                    <>
                      <span>{Math.min(...probabilityData.map(d => d.probability)).toFixed(4)}</span>
                      <span>{Math.max(...probabilityData.map(d => d.probability)).toFixed(4)}</span>
                    </>
                  ) : hazardData.length > 0 ? (
                    <>
                      <span>{Math.min(...hazardData.map(d => d.hazard)).toFixed(4)}</span>
                      <span>{Math.max(...hazardData.map(d => d.hazard)).toFixed(4)}</span>
                    </>
                  ) : (
                    <>
                      <span>No data</span>
                      <span></span>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Controllo coordinate in basso al centro */}
      {mouseCoordinates && (
        <div 
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '6px',
            fontSize: '14px',
            fontFamily: 'monospace',
            zIndex: 1000,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none',
            minWidth: '200px',
            textAlign: 'center'
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Coordinates</div>
          <div>
            Lat: {mouseCoordinates.lat.toFixed(6)}° | 
            Lon: {mouseCoordinates.lon.toFixed(6)}°
          </div>
        </div>
      )}
      
      {loading && (
        <div className="loading" style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '12px',
          boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)'
        }}>
          <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px', color: '#ffffff' }}></i>
          Loading hazard data...
        </div>
      )}
      
      {error && (
        <div className="error" style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          backgroundColor: 'rgba(220, 53, 69, 0.9)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '6px',
          maxWidth: '80%'
        }}>
          <strong><i className="fas fa-exclamation-triangle"></i> Error:</strong> {error}
        </div>
      )}

      {!loading && !error && probabilityData.length === 0 && hazardData.length === 0 && lavaData.length === 0 && seismicData.length === 0 && !seismicWmsData && (
        <div className="loading" style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          backgroundColor: 'rgba(108, 117, 125, 0.9)',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
          No data to display. Try different parameters.
        </div>
      )}
    </div>
  );
};

export default HazardMap;